import React from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { SlidingArrowButton } from 'components/CustomButton';
import CaseMasterSignupDialog from 'dialogs/CaseMasterSignupDialog';

const useStyles = makeStyles((theme) => ({
  heroContainer: {
    width: '100%',
    minHeight: 'calc(100vh - 128px)',
    background: 'linear-gradient(180deg, #0A3F63 0%, #122A3A 100%)',
    boxShadow: '0 4.449px 4.449px 0 rgba(0, 0, 0, 0.25)',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('xl')]: {
      // minHeight: 'auto',
      paddingBottom: theme.spacing(10),
      minHeight: 'calc(100vh - 108px)',
    },
    [theme.breakpoints.down('md')]: {
      minHeight: 'auto',
      paddingBottom: theme.spacing(10),
      //  minHeight: 'calc(100vh - 108px)',
    },
  },
  navBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(4, 15),
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(3, 8),
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4),
      flexWrap: 'wrap',
      gap: theme.spacing(2),
    },
  },
  logo: {
    ...theme.typography['h4-bold'],
    color: theme.palette.shades['clr-white-900'],
    cursor: 'pointer',
    [theme.breakpoints.down('md')]: {
      ...theme.typography['h5-bold'],
    },
  },
  navMenu: {
    display: 'flex',
    gap: theme.spacing(6),
    alignItems: 'center',
    [theme.breakpoints.down('lg')]: {
      gap: theme.spacing(4),
    },
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  navItem: {
    ...theme.typography['body01-medium'],
    color: theme.palette.shades['clr-white-900'],
    cursor: 'pointer',
    transition: 'opacity 0.3s ease',
    '&:hover': {
      opacity: 0.8,
    },
  },
  navButtons: {
    display: 'flex',
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  navButton: {
    ...theme.typography['body01-medium'],
    color: theme.palette.shades['clr-white-900'],
    border: `1px solid ${theme.palette.shades['clr-white-900']}`,
    borderRadius: '8px',
    padding: theme.spacing(1.5, 4),
    textTransform: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  contentSection: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(10, 37.5),
    flex: 1,
    [theme.breakpoints.down('xl')]: {
      padding: theme.spacing(10, 15),
    },
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(8, 15),
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      padding: theme.spacing(6, 10),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(6, 5),
    },
  },
  leftContent: {
    width: '100%',
    maxWidth: '1120px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    alignItems: 'center',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
    },
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.shades['clr-white-900'],
    borderRadius: '20px',
    padding: theme.spacing(1, 2.5),
    width: 'fit-content',
    ...theme.typography['body02-medium'],
    color: theme.palette.primary.main,
  },
  mainHeading: {
    ...theme.typography['h1-semiBold'],
    color: theme.palette.shades['clr-white-900'],
    // Each phrase stays on a single line. The longest sentence the typing
    // animation can produce ("Solve past interview Founder's Office cases")
    // is ~22em wide, so the font size is derived from the width left over
    // after this section's horizontal padding instead of being fixed at 48px.
    whiteSpace: 'nowrap',
    fontSize: 'min(calc((100vw - 300px) / 22), 48px)',
    '& .highlight': {
      color: '#7C90FF',
    },
    [theme.breakpoints.down('xl')]: {
      fontSize: 'min(calc((100vw - 120px) / 22), 48px)',
    },
    [theme.breakpoints.down('md')]: {
      fontSize: 'calc((100vw - 80px) / 22)',
    },
    [theme.breakpoints.down('sm')]: {
      // On phones a single line would shrink the heading below ~15px, so the
      // sentence is split into three fixed lines (see `headingPhrase`); each
      // one still fits without wrapping at this size.
      fontSize: 'clamp(22px, 7vw, 32px)',
    },
  },
  // Halves of the first sentence: inline (one line) everywhere except phones,
  // where each half takes its own line.
  headingPhrase: {
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  subHeading: {
    ...theme.typography['body01-regular'],
    color: theme.palette.shades['clr-white-900'],
  },
  subSubHeading: {
    ...theme.typography['body02-regular'],
    color: theme.palette.shades['clr-white-900'],
    opacity: 0.8,
  },
  ctaButton: {
    backgroundColor: '#4169E1',
    color: theme.palette.shades['clr-white-900'],
    ...theme.typography['h6-medium'],
    padding: theme.spacing(2, 5),
    borderRadius: '8px',
    textTransform: 'none',
    width: 'fit-content',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#3558C8',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(65, 105, 225, 0.4)',
    },
  },
  studentCount: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    ...theme.typography['body01-medium'],
    color: theme.palette.shades['clr-white-900'],
  },
  statsBar: {
    background: 'linear-gradient(90deg, #FFF 0%, #E5E8FF 50.48%, #D7DBFF 100%)',
    borderRadius: '8px',
    padding: theme.spacing(5, 8),
    margin: theme.spacing(0, 37.5, 5, 37.5),
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(4),
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
    minHeight: '105px',
    [theme.breakpoints.down('xl')]: {
      margin: theme.spacing(0, 15, 5, 15),
      padding: theme.spacing(4, 6),
    },
    [theme.breakpoints.down('lg')]: {
      margin: theme.spacing(0, 15, 4, 15),
      padding: theme.spacing(4, 6),
    },
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(0, 10, 3, 10),
      padding: theme.spacing(3, 4),
      gap: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0, 5, 3, 5),
    },
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.spacing(0.5),
    [theme.breakpoints.down('md')]: {
      flex: '1 1 30%',
    },
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 100%',
    },
  },
  statValue: {
    ...theme.typography['h6-semiBold'],
    color: theme.palette.neutral['clr-900'],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '18.5px',
    lineHeight: '22.2px',
    [theme.breakpoints.down('md')]: {
      ...theme.typography['body01-semiBold'],
    },
  },
  statValueUnderline: {
    textDecoration: 'underline',
    textUnderlinePosition: 'from-font',
  },
  statLabel: {
    ...theme.typography['caption-regular'],
    color: theme.palette.neutral['clr-600'],
    fontSize: '12.95px',
    lineHeight: '18.5px',
    [theme.breakpoints.down('md')]: {
      fontSize: '11px',
    },
  },
  starIcon: {
    color: '#0056D2',
    fontSize: '20px',
  },
  gridImage: {
    position: 'absolute',
    width: '186px',
    height: '286px',
    overflow: 'visible',
    display: 'block',
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  },
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
  cursor: {
    animation: '$blink 0.7s step-end infinite',
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
}));

// Words cycled through by the two typed animations in the hero heading.
const CASE_TYPES = ['Consulting', 'Product', "Founder's Office", 'EIR'];
const COMPANIES = ['BCG', 'Amazon', 'Google', 'Zomato', 'Netflix'];
const TYPING_SPEED = 100;
const DELETING_SPEED = 60;
const HOLD_DURATION = 2000;
const PAUSE_DURATION = 500;
const CaseHeroSection = () => {
  const classes = useStyles();
  const [openSignupDialog, setOpenSignupDialog] = React.useState(false);
  const [typingText, setTypingText] = React.useState({ caseType: '', company: '' });
  const [wordIndex, setWordIndex] = React.useState({ caseType: 0, company: 0 });
  const [isDeleting, setIsDeleting] = React.useState(false);

  React.useEffect(() => {
    const caseTypeWord = CASE_TYPES[wordIndex.caseType % CASE_TYPES.length];
    const companyWord = COMPANIES[wordIndex.company % COMPANIES.length];

    let timeout;
    const { caseType, company } = typingText;
    const caseTypeDone = caseType.length === caseTypeWord.length;
    const companyDone = company.length === companyWord.length;

    if (!isDeleting) {
      if (!caseTypeDone || !companyDone) {
        timeout = setTimeout(() => {
          setTypingText({
            caseType: caseTypeDone ? caseType : caseTypeWord.slice(0, caseType.length + 1),
            company: companyDone ? company : companyWord.slice(0, company.length + 1),
          });
        }, TYPING_SPEED);
      } else {
        timeout = setTimeout(() => setIsDeleting(true), HOLD_DURATION);
      }
    } else {
      if (caseType.length > 0 || company.length > 0) {
        timeout = setTimeout(() => {
          setTypingText({
            caseType: caseType.length > 0 ? caseTypeWord.slice(0, caseType.length - 1) : '',
            company: company.length > 0 ? companyWord.slice(0, company.length - 1) : '',
          });
        }, DELETING_SPEED);
      } else {
        // Finished deleting both words — advance each field to its next word.
        timeout = setTimeout(() => {
          setWordIndex((prev) => ({
            caseType: (prev.caseType + 1) % CASE_TYPES.length,
            company: (prev.company + 1) % COMPANIES.length,
          }));
          setIsDeleting(false);
        }, PAUSE_DURATION);
      }
    }

    return () => clearTimeout(timeout);
  }, [typingText, isDeleting, wordIndex]);

  // Each heading phrase sits on its own line; the phrase itself is kept on a
  // single line by the responsive font size in `mainHeading`.
  const headingLineStyle = { display: 'block' };
  // Keeps multi-word options like "Founder's Office" on one line.
  const typedWordStyle = { fontStyle: 'italic', whiteSpace: 'nowrap' };

  const statsData = [
    { value: '6+ Parameters for<br />Evaluation', underline: false, singleLine: true },
    { value: '4.6', label: '(1,467 reviews)', showStar: true },
    { value: '150+ Cases<br />Available', singleLine: true },
  ];

  return (
    <Box className={classes.heroContainer}>
      {/* Grid Background Images - Only visible on lg and above */}
      <Box
        component="img"
        src="https://languify-assets-v02.s3.ap-south-1.amazonaws.com/images/grid-right.png"
        alt="grid decoration"
        className={classes.gridImage}
        sx={{ top: -80, right: 0 }}
      />
      <Box
        component="img"
        src="https://languify-assets-v02.s3.ap-south-1.amazonaws.com/images/grid-right.png"
        alt="grid decoration"
        className={classes.gridImage}
        sx={{ bottom: -10, right: 0 }}
      />
      <Box
        component="img"
        src="https://languify-assets-v02.s3.ap-south-1.amazonaws.com/images/grid-right.png"
        alt="grid decoration"
        className={classes.gridImage}
        sx={{ bottom: -10, left: 0, rotate: '180deg' }}
      />

      {/* Main Content Section */}
      <Box className={classes.contentSection}>
        {/* Left Content */}
        <Box className={classes.leftContent}>

          <Box className={classes.badge}>
            <StarIcon sx={{ fontSize: '14px', color: '#D86D72' }} />
            Trusted by 6000+ aspirants
          </Box>

          <Typography className={classes.mainHeading}>
            <span style={headingLineStyle}>
              <span className={classes.headingPhrase}>Solve past interview</span>
              {' '}
              <span className={classes.headingPhrase}>
                <span className="highlight" style={typedWordStyle}>
                  {typingText.caseType}<span className={classes.cursor}>|</span>
                </span>
                {' '}cases
              </span>
            </span>
            <span style={headingLineStyle}>
              for{' '}
              <span className="highlight" style={typedWordStyle}>
                {typingText.company}<span className={classes.cursor}>|</span>
              </span>
            </span>
          </Typography>

          <Typography className={classes.subHeading}>
            Master every case, every framework which was actually asked in a past interview with Case Master AI.
          </Typography>
          {/* <Typography className={classes.subSubHeading}>
            Get instant, structured feedback from an AI that thinks like a McKinsey or BCG partner.
          </Typography> */}
          <Box style={{ position: 'relative' }}>
            <SlidingArrowButton
              label="Practice Case for free"
              bgColor='#184EFF'
              variant="outlined"
              width="301px"
              sx={{ textTransform: 'none' }}
              onClick={() => setOpenSignupDialog(true)}
            />
            <img
              src={`${process.env.REACT_APP_ASSETS_URL}/images/sprial_arrow.svg`}
              alt="arrow"
              style={{ position: 'absolute', top: '-35px', left: '310px' }}
            />
          </Box>


        </Box>
      </Box>

      {/* Stats Bar */}
      <Box className={classes.statsBar}>
        {statsData.map((stat, index) => (
          <Box key={index} className={classes.statItem}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Typography
                className={`${classes.statValue} ${stat.underline ? classes.statValueUnderline : ''}`}
                style={stat.color ? { color: stat.color } : {}}
                dangerouslySetInnerHTML={{ __html: stat.value }}
              />
              {stat.showStar && <StarIcon className={classes.starIcon} />}
            </Box>
            {!stat.singleLine && stat.label && (
              <Typography
                className={classes.statLabel}
                style={stat.labelColor ? { color: stat.labelColor } : {}}
              >
                {stat.label}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {/* Signup Dialog */}
      <CaseMasterSignupDialog
        open={openSignupDialog}
        onClose={() => setOpenSignupDialog(false)}
      />
    </Box>
  );
};

export default CaseHeroSection;
